import React, { useState, useEffect } from 'react';
import cn from 'classnames';

import Button from '../../../components/common/ui/Button';
import { getDeviceDataFromMapping } from '../../../utils/devices';
import {
  getAccountNotEligibleForSelfActivation,
  getPlanVariants,
} from './utils';
import ActivationCustomersPlansList from './ActivationCustomersPlansList';
import ActivationBox from '../ActivationBox';

import styles from './styles.module.scss';
import {
  filterSubCharges,
  findVariantCharge,
  needChargeForProduct,
} from '../utils';
import { getDeviceSn } from '../../../utils/helpers';

const ActivationSelectSub = ({
  subscriptions,
  sub,
  setSub,
  subsLoading,
  device,
  subsCharges,
  openCompleteScreen,
  openProcessingScreen,
  openSelectPlanScreen,
  openErrorScreen,
  activateSubscription,
}) => {
  // prevent ui glitch from updating recharge sub
  // new values need only for the next line activation
  // which will remount component with new subs list
  const [subsList] = useState(subscriptions);
  const { planVariants, newLinePlanVariants } = getPlanVariants(subsList);
  const [selectedVariant, setSelectedVariant] = useState(
    planVariants?.length === 1 ? planVariants[0] : null,
  );
  const accountNotEligible =
    getAccountNotEligibleForSelfActivation(subsCharges);

  const showChoosePlanButton = Boolean(
    newLinePlanVariants.length && !planVariants.length,
  );
  const showNewLineButton = Boolean(
    newLinePlanVariants.length && planVariants.length && !accountNotEligible,
  );
  const showActivateButton = !showChoosePlanButton;

  const [error, setError] = useState('');

  const dataFromMapping = getDeviceDataFromMapping(
    device.device_info.device_type_name,
  );
  const deviceTitle = dataFromMapping.title;
  const deviceImagePath = dataFromMapping.activationImagePath;

  const handleSelectVariant = (variant) => {
    if (selectedVariant?.name !== variant.name) {
      let sub;
      if (variant.deviceSn) {
        sub = subsList.find(
          (sub) => getDeviceSn(sub.properties) === variant.deviceSn,
        );
      } else {
        sub = subsList.find((sub) => sub.variant_title === variant.name);
      }
      setSelectedVariant(variant);
      setSub(sub);
    }
  };

  const handleNewLine = () => {
    const sub = subsList.find(
      (sub) => sub.variant_title === newLinePlanVariants[0].name,
    );
    setSub(sub);
    openSelectPlanScreen();
  };

  const handleActivate = () => {
    const isPaymentRequired = needChargeForProduct(sub);
    if (isPaymentRequired) {
      const subCharges = filterSubCharges(subsCharges, sub.id);
      const charge = findVariantCharge(
        subCharges,
        sub.id,
        sub.external_variant_id?.ecommerce,
        'success',
      );
      if (!charge) {
        openSelectPlanScreen();
        return;
      }
    }

    activateSubscription({
      variant: selectedVariant,
      setError,
      openErrorScreen,
      openCompleteScreen,
      openProcessingScreen,
    });
  };

  useEffect(() => {
    if (!subsList.length && !subsLoading) {
      openErrorScreen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subsLoading]);

  useEffect(() => {
    if (planVariants?.length === 1) {
      const sub = subsList.find(
        (sub) => sub.variant_title === planVariants[0].name,
      );
      setSub(sub);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subsList]);

  return (
    <ActivationBox
      className={styles.wrapper}
      notCentered={planVariants.length > 2}
    >
      <img src={deviceImagePath} alt="" className={styles.deviceImage} />
      <div className={styles.content}>
        <div className={styles.deviceIdentified}>Device identified.</div>
        <h3 className={cn('h3', styles.title)}>{deviceTitle}</h3>
        <ActivationCustomersPlansList
          planVariants={planVariants}
          newLinePlanVariants={newLinePlanVariants}
          selectedVariant={selectedVariant}
          handleSelectVariant={handleSelectVariant}
        />
        {showChoosePlanButton && (
          <Button
            onClick={handleNewLine}
            className={cn(styles.button, styles.chooseButton)}
          >
            Choose a plan
          </Button>
        )}
        {showNewLineButton && (
          <Button
            colorType="grey"
            onClick={handleNewLine}
            className={cn(styles.button, styles.newPlanButton)}
          >
            Buy a new plan
          </Button>
        )}
        {showActivateButton && (
          <Button
            onClick={handleActivate}
            className={styles.button}
            disabled={!selectedVariant || accountNotEligible}
          >
            Activate
          </Button>
        )}
        <p className={cn('text-error', styles.error)}>{error}</p>
        {accountNotEligible && (
          <p className={cn('text-error', styles.error)}>
            You account is not eligible for the activation. Please, contact
            customer support.
          </p>
        )}
      </div>
    </ActivationBox>
  );
};

export default ActivationSelectSub;
